<!--
  ~ Copyright (c) GM Global Technology Operations LLC. All rights reserved.
  ~ This information is confidential and proprietary to GM Global Technology
  ~ Operations LLC and may not be used, modified, copied or distributed.
  -->

<div id="segment-screen-container">
  <fab-loading-spinner color="primary" *ngIf="childSpinner || showSpinner"
                       id="loading-spinner"></fab-loading-spinner>

  <div id="header-container">
    <div>
      <h1 id="header-text">Manage Segment Schemes</h1>
    </div>
    <div>
      <button mat-raised-button color="primary" id="report-button" class="action-button" (click)="downloadReport()">
        Download Segment Report
      </button>
    </div>
  </div>

  <div id="segments-container">
    <div class="segment-scheme-management">
      <div class="card-and-actions">
        <div class="segment-div">
          <mat-card appearance="outlined" class="mat-card-container default-padding">
            <mat-card-title>Available Segments
              <button mat-raised-button color="primary" id="segment-create-button" class="create-button"
                      (click)="openSegmentCreationDialog()">
                Create Segment
              </button>
            </mat-card-title>

            <mat-tree [dataSource]="unmappedSegmentsDatasource" [treeControl]="unmappedSegmentsTreeControl"
                      id="unmapped-tree" class="segment-tree">
              <!-- This is the tree node template for leaf nodes -->
              <!-- There is inline padding applied to this node using styles.
                This padding value depends on the mat-icon-button width. -->
              <mat-tree-node *matTreeNodeDef="let node;" matTreeNodeToggle class="segment-node">
                <div class="segment-text">
                  {{ node.segment }}
                </div>
                <div class="veh-type">
                  {{ node.vehType }}
                </div>
              </mat-tree-node>
              <!-- This is the tree node template for expandable nodes -->
              <mat-nested-tree-node *matTreeNodeDef="let node; when: hasChild" (click)="selectSegment(node)">
                <!-- Style nested nodes based on Segment attributes -->
                <div [class.top-level-node]="node.lvl === 1"
                     [class.selected-node]="node === selectedSegment"
                     class="segment-node">
                  <div class="chevron-segment-text-container">
                    <button mat-icon-button matTreeNodeToggle
                            [attr.aria-label]="'Toggle ' + node.segment">
                      <mat-icon class="mat-icon-rtl-mirror">
                        {{ unmappedSegmentsTreeControl.isExpanded(node) ? 'expand_more' : 'chevron_right' }}
                      </mat-icon>
                    </button>
                    <div class="segment-text">
                      {{ node.segment }}
                    </div>
                  </div>
                  <div class="veh-type">
                    {{ node.vehType }}
                    <button mat-icon-button color="warn" (click)="deleteSegment(node)">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </div>
                </div>
                <!-- There is inline padding applied to this div using styles.
                    This padding value depends on the mat-icon-button width.  -->
                <div [class.segment-tree-invisible]="!unmappedSegmentsTreeControl.isExpanded(node)"
                     role="group">
                  <ng-container matTreeNodeOutlet></ng-container>
                </div>
              </mat-nested-tree-node>
            </mat-tree>
          </mat-card>
        </div>

        <div class="actions-container">
          <button mat-icon-button
                  matTooltip="Move selected segment to GM Global Scheme"
                  [disabled]="!enableMoveSegmentToScheme()"
                  (click)="moveSegmentToScheme()"
                  id="add-mapping"
                  class="segment-action-icon">
            <mat-icon>keyboard_double_arrow_right</mat-icon>
          </button>
          <button mat-icon-button
                  matTooltip="Remove selected segment from GM Global Scheme"
                  [disabled]="!enableMoveSegmentFromScheme()"
                  (click)="moveSegmentFromScheme()"
                  id="delete-mapping"
                  class="segment-action-icon">
            <mat-icon>keyboard_double_arrow_left</mat-icon>
          </button>
        </div>
      </div>
      <div class="card-and-actions">
        <div class="segment-div">
          <mat-card appearance="outlined" class="mat-card-container default-padding">
            <mat-card-title>GM Global</mat-card-title>
            <mat-tree [dataSource]="mappedSegmentsDatasource" [treeControl]="mappedSegmentsTreeControl"
                      id="mapped-tree" class="segment-tree">
              <!-- This is the tree node template for leaf nodes -->
              <!-- There is inline padding applied to this node using styles.
                This padding value depends on the mat-icon-button width. -->
              <mat-tree-node *matTreeNodeDef="let node;" matTreeNodeToggle class="segment-node">
                <div>
                  {{ node.segment }}
                </div>
                <div class="veh-type">
                  {{ node.vehType }}
                </div>
              </mat-tree-node>
              <!-- This is the tree node template for expandable nodes -->
              <mat-nested-tree-node *matTreeNodeDef="let node; when: hasChild" (click)="selectSegment(node)">
                <div [class.top-level-node]="node.lvl === 1"
                     [class.selected-node]="node === selectedSegment"
                     class="segment-node">
                  <div class="chevron-segment-text-container">
                    <button mat-icon-button matTreeNodeToggle
                            [attr.aria-label]="'Toggle ' + node.segment">
                      <mat-icon class="mat-icon-rtl-mirror">
                        {{ mappedSegmentsTreeControl.isExpanded(node) ? 'expand_more' : 'chevron_right' }}
                      </mat-icon>
                    </button>
                    <div class="segment-text">
                      {{ node.segment }}
                    </div>
                  </div>
                  <div class="veh-type">
                    {{ node.vehType }}
                  </div>
                </div>
                <!-- There is inline padding applied to this div using styles.
                    This padding value depends on the mat-icon-button width.  -->
                <div [class.segment-tree-invisible]="!mappedSegmentsTreeControl.isExpanded(node)"
                     role="group">
                  <ng-container matTreeNodeOutlet></ng-container>
                </div>
              </mat-nested-tree-node>
            </mat-tree>
          </mat-card>
        </div>
        <div class="actions-container">
          <button mat-icon-button
                  matTooltip="Move selected segment up the segment order"
                  class="segment-action-icon"
                  id="move-segment-up"
                  [disabled]="!enableSegmentOrderUp()"
                  (click)="moveSegmentUp()">
            <mat-icon>arrow_upward</mat-icon>
          </button>
          <button mat-icon-button
                  matTooltip="Move selected segment down the segment order"
                  class="segment-action-icon"
                  id="move-segment-down"
                  [disabled]="!enableSegmentOrderDown()"
                  (click)="moveSegmentDown()">
            <mat-icon>arrow_downward</mat-icon>
          </button>
        </div>
      </div>
    </div>

    <div class="segment-div">
      <app-segment-suffix-management (spinnerEvent)="childSpinner=$event"
                                     (dataChangeEvent)="loadSegmentDatasources()"></app-segment-suffix-management>
    </div>
  </div>
</div>

